import { events$ } from '@shared/services'
import { useRoute } from 'vue-router'
import { BasketUtil } from '@shared/utils/Basket'

import basketStore from '@shared/store/basket'
import partnerStore from '@shared/store/partner'
import productStructureStore from '@shared/store/productStructure'

import useApplication from '@shared/hooks/useApplication'
import useChatBot from '@shared/hooks/useChatBot'
import useContract from '@shared/hooks/useContract'
import useI18n from '@shared/hooks/useI18n'
import useIncentive from '@shared/hooks/useIncentive'
import usePerson from '@shared/hooks/usePerson'
import usePersonDetails from '@shared/hooks/usePersonDetails'
import useSnackbar from '@shared/hooks/useSnackbar'

import { ROUTE_NAME } from '@shared/config/constants'
import { EVENT_BASKET, EVENT_PRODUCT, EVENT_URL } from '@shared/config/events'

const routeExcludeList = [ROUTE_NAME.BASKET]

export function registerUpdateBasketListeners() {
  // HOOKS
  const { isWww } = useApplication()
  const { updateChatBot } = useChatBot()
  const { installContractOnUpdate } = useContract()
  const { installIncentiveOnUpdate } = useIncentive()
  const { getPerson } = usePerson()

  function getPartnerPayload() {
    const contractOwner = getPerson(basketStore.basket.contractOwner?.personId)
    const partnerNumber = contractOwner?.contractNumber
      ? BasketUtil.getCustomerNumberFromContract(contractOwner.contractNumber)
      : null

    return {
      contractStartDate: basketStore.basket.contractStartDate,
      language: basketStore.basket.language,
      distributionPartnerNumber: partnerStore.basketDistributionPartnerNumber.value,
      collectiveNumber: basketStore.basket.collective?.contractNumber,
      partnerNumber,
    }
  }

  events$.once(EVENT_BASKET.CREATED, () => {
    partnerStore.install(getPartnerPayload())
  })

  events$.on(EVENT_BASKET.UPDATED, ({ basket, originalBasket }) => {
    partnerStore.installOnUpdate(getPartnerPayload(), originalBasket)

    productStructureStore.installProductStructureOnUpdate(basket, originalBasket)

    installContractOnUpdate(basket, originalBasket)

    updateChatBot(basket, originalBasket)

    // @note: ain't nobody got money for them calls (on dotcom)
    if (!isWww.value) {
      installIncentiveOnUpdate(basket, originalBasket)
    }
  })
}

export function registerBasketActionListeners(router) {
  // HOOKS
  const { isWww } = useApplication()
  const route = !isWww.value ? useRoute() : false
  const { t } = useI18n()
  const { getPersonWithDetails } = usePersonDetails()
  const { addNotification } = useSnackbar()

  events$.on(EVENT_PRODUCT.ADDED, data => {
    if (data.silent) return

    const person = getPersonWithDetails(data.personId)
    const payload = {
      persistent: false,
      text: t(
        'basket.events.add',
        {
          product: t(`products.${data.productId}.name`),
          firstName: person.firstName,
        },
        'basic'
      ),
    }

    if (!routeExcludeList.includes(router?.currentRoute.value?.name)) {
      payload.action = {
        label: t('basket.gotoBasket'),
        callback: () => {
          events$.emit(EVENT_URL.GOTO, {
            type: 'push',
            route: {
              name: ROUTE_NAME.BASKET,
              path: 'basket/',
            },
          })
        },
      }
    }

    if (!route?.meta?.aide) {
      addNotification(payload)
    }
  })

  events$.on(EVENT_PRODUCT.REMOVED, data => {
    if (data.silent) return

    const person = getPersonWithDetails(data.personId)
    const payload = {
      persistent: false,
      text: t(
        'basket.events.remove',
        {
          product: t(`products.${data.productId}.name`),
          firstName: person.firstName,
        },
        'basic'
      ),
    }

    if (!routeExcludeList.includes(router?.currentRoute.value?.name)) {
      payload.action = {
        label: t('basket.gotoBasket'),
        callback: () => {
          events$.emit(EVENT_URL.GOTO, {
            type: 'push',
            route: {
              name: ROUTE_NAME.BASKET,
              path: 'basket/',
            },
          })
        },
      }
    }

    if (!route?.meta?.aide) {
      addNotification(payload)
    }
  })

  events$.on(EVENT_PRODUCT.CHANGED, data => {
    if (data.silent) return

    const person = getPersonWithDetails(data.personId)
    const payload = {
      persistent: false,
      text: t(
        'basket.events.change',
        {
          product: t(`products.${data.productId}.name`),
          firstName: person.firstName,
        },
        'basic'
      ),
    }

    if (!routeExcludeList.includes(router?.currentRoute.value?.name)) {
      payload.action = {
        label: t('basket.gotoBasket'),
        callback: () => {
          events$.emit(EVENT_URL.GOTO, {
            type: 'push',
            route: {
              name: ROUTE_NAME.BASKET,
              path: 'basket/',
            },
          })
        },
      }
    }

    if (!route?.meta?.aide) {
      addNotification(payload)
    }
  })
}
